/*
  Elite Fanstore - Favoris (assets/elite-favoris.js).
  La liste vit dans le navigateur du client (localStorage « elite-favoris ») ; client connecté : elle est
  aussi enregistrée sur son compte (métachamp custom.favoris) via l'app proxy /apps/favoris (Vercel,
  Favoris/serveur). 1re connexion sur un appareil = fusion, ensuite la liste du compte fait foi.
  Format :
  [{ id: "<product id>", h: "<handle>", t: <horodatage> }], le plus récent en premier.
  - Cœurs : tout élément [data-ef-fav] (snippets/elite-favori-coeur.liquid), même ajouté plus tard
    (filtres, « voir plus », recommandations) : un MutationObserver les remet à jour.
  - En-tête : [data-ef-compte] reçoit le nombre de favoris.
  - Page « Mes favoris » : [data-ef-page] (sections/elite-favoris.liquid). Chaque carte est rendue
    par le thème via /products/<handle>?section_id=elite-favori-carte (même carte que les collections).
*/
(() => {
  if (window.eliteFavorisPret) return;
  window.eliteFavorisPret = true;

  const CLE = 'elite-favoris';
  const MAX = 100;
  const cfg = window.eliteFavoris || {};
  const T = cfg.textes || {};
  const racine = cfg.racine || '/';
  const pageFavoris = cfg.page || '/pages/favoris';

  // ---------- Stockage ----------
  let memoire = []; // repli si le navigateur bloque le stockage (navigation privée stricte)
  function lire() {
    try {
      const brut = JSON.parse(localStorage.getItem(CLE) || '[]');
      memoire = Array.isArray(brut) ? brut.filter(f => f && f.id && f.h).map(f => ({ id: String(f.id), h: String(f.h), t: f.t || 0 })) : [];
    } catch (e) { /* stockage indisponible : on garde la mémoire de la page */ }
    return memoire.slice();
  }
  function ecrire(liste) {
    memoire = liste.slice(0, MAX);
    try { localStorage.setItem(CLE, JSON.stringify(memoire)); } catch (e) { /* idem */ }
    synchroniser();
    envoyer();
  }
  const index = (liste, id) => liste.findIndex(f => f.id === String(id));

  function ajouter(id, h, position) {
    const liste = lire();
    if (index(liste, id) >= 0) return;
    const pos = Number.isInteger(position) ? Math.min(position, liste.length) : 0;
    liste.splice(pos, 0, { id: String(id), h: String(h), t: Date.now() });
    ecrire(liste);
  }
  function retirer(id) {
    const liste = lire();
    const i = index(liste, id);
    if (i < 0) return null;
    const [ancien] = liste.splice(i, 1);
    ecrire(liste);
    return { ancien, position: i };
  }

  // ---------- Compte client (app proxy) ----------
  const PROXY = '/apps/favoris';
  const CLE_SYNC = 'elite-favoris-compte'; // id du client déjà fusionné sur ce navigateur
  let minuterie = null;
  let compteLu = false; // on n'écrit pas sur le compte avant de l'avoir lu (sinon on l'écraserait)
  function envoyer() {
    if (!cfg.client || !compteLu) return;
    clearTimeout(minuterie);
    minuterie = setTimeout(() => {
      fetch(PROXY, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ liste: memoire }), keepalive: true })
        .catch(() => { /* hors ligne : la liste reste en local, renvoyée au prochain changement */ });
    }, 800);
  }
  async function chargerCompte() {
    if (!cfg.client) return;
    try {
      const r = await fetch(PROXY, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const d = await r.json();
      if (!r.ok || !d.connecte || !Array.isArray(d.liste)) return;
      let dejaFusionne = false;
      try { dejaFusionne = localStorage.getItem(CLE_SYNC) === String(cfg.client); } catch (e) { /* */ }
      let liste = d.liste;
      if (!dejaFusionne) {
        const ids = new Set(liste.map(f => String(f.id)));
        liste = liste.concat(lire().filter(f => !ids.has(f.id))).sort((a, b) => (b.t || 0) - (a.t || 0));
      }
      compteLu = true;
      try { localStorage.setItem(CLE_SYNC, String(cfg.client)); } catch (e) { /* */ }
      const avant = JSON.stringify(memoire);
      memoire = liste.slice(0, MAX);
      try { localStorage.setItem(CLE, JSON.stringify(memoire)); } catch (e) { /* */ }
      if (JSON.stringify(memoire) !== avant) synchroniser();
      if (JSON.stringify(memoire) !== JSON.stringify(d.liste)) envoyer();
    } catch (e) { /* serveur indisponible : on reste en local */ }
  }

  // ---------- Affichage des cœurs et du compteur ----------
  function synchroniser() {
    const liste = memoire;
    const ids = new Set(liste.map(f => f.id));
    document.querySelectorAll('[data-ef-fav]').forEach(b => {
      const actif = ids.has(b.getAttribute('data-ef-fav'));
      if (b.getAttribute('aria-pressed') !== String(actif)) b.setAttribute('aria-pressed', String(actif));
    });
    document.querySelectorAll('[data-ef-compte]').forEach(el => {
      const n = liste.length;
      el.textContent = n > 99 ? '99+' : String(n);
      el.hidden = n === 0;
    });
    document.dispatchEvent(new CustomEvent('elite-favoris:change', { detail: { liste: liste.slice() } }));
  }

  function rejouer(el, classe) {
    if (!el) return;
    el.classList.remove(classe);
    void el.offsetWidth; // relance l'animation
    el.classList.add(classe);
  }

  // ---------- Message de confirmation ----------
  let toast = null;
  let minuteur = null;
  function afficherToast(texte, action) {
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ef-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = '';
    const span = document.createElement('span');
    span.className = 'ef-toast__texte';
    span.textContent = texte;
    toast.appendChild(span);
    if (action && action.href) {
      const a = document.createElement('a');
      a.className = 'ef-toast__action';
      a.href = action.href;
      a.textContent = action.label;
      toast.appendChild(a);
    } else if (action && action.onClick) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ef-toast__action';
      b.textContent = action.label;
      b.addEventListener('click', () => { action.onClick(); cacherToast(); });
      toast.appendChild(b);
    }
    requestAnimationFrame(() => toast.classList.add('ef-toast--visible'));
    clearTimeout(minuteur);
    minuteur = setTimeout(cacherToast, 4000);
  }
  function cacherToast() {
    clearTimeout(minuteur);
    if (toast) toast.classList.remove('ef-toast--visible');
  }

  // ---------- Clic sur un cœur ----------
  // En capture : le cœur est posé sur des cartes cliquables, le clic ne doit jamais ouvrir le produit.
  document.addEventListener('click', e => {
    const bouton = e.target.closest && e.target.closest('[data-ef-fav]');
    if (!bouton) return;
    e.preventDefault();
    e.stopPropagation();
    const id = bouton.getAttribute('data-ef-fav');
    const h = bouton.getAttribute('data-ef-handle');
    const surPageFavoris = !!document.querySelector('[data-ef-page]');
    if (index(lire(), id) >= 0) {
      const retrait = retirer(id);
      afficherToast(T.retire || 'Retiré de vos favoris', retrait && {
        label: T.annuler || 'Annuler',
        onClick: () => ajouter(retrait.ancien.id, retrait.ancien.h, retrait.position)
      });
    } else {
      ajouter(id, h);
      rejouer(bouton, 'ef-fav--pop');
      document.querySelectorAll('.header__icon--favoris').forEach(el => rejouer(el, 'ef-entete--pop'));
      afficherToast(T.ajoute || 'Ajouté à vos favoris', surPageFavoris ? null : { label: T.voir || 'Voir', href: pageFavoris });
    }
  }, true);

  // ---------- Page « Mes favoris » ----------
  const cacheCartes = new Map(); // id -> HTML de la carte, pour réafficher sans recharger (Annuler)
  let fileAttente = [];
  let enCours = 0;

  function pageFav() { return document.querySelector('[data-ef-page]'); }

  function carteAttente(id) {
    const li = document.createElement('li');
    li.className = 'grid__item ef-page__item ef-page__item--attente';
    li.setAttribute('data-ef-item', id);
    li.innerHTML = '<div class="ef-page__squelette" aria-hidden="true"><span></span><span></span><span></span></div>';
    return li;
  }

  function remplir(li, html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const carte = tpl.content.querySelector('[data-ef-carte]');
    if (!carte) return false;
    li.classList.remove('ef-page__item--attente');
    li.replaceChildren(...carte.childNodes);
    return true;
  }

  async function charger(fav) {
    const essais = [racine, '/'].filter((r, i, a) => a.indexOf(r) === i);
    for (const r of essais) {
      try {
        const rep = await fetch(`${r}products/${encodeURIComponent(fav.h)}?section_id=elite-favori-carte`, { credentials: 'same-origin' });
        if (rep.status === 404) continue;
        if (!rep.ok) return { erreur: true };
        const html = await rep.text();
        if (!html.includes('data-ef-carte')) continue;
        return { html };
      } catch (e) {
        return { erreur: true };
      }
    }
    return { disparu: true };
  }

  function lancerChargements() {
    while (enCours < 4 && fileAttente.length) {
      const fav = fileAttente.shift();
      enCours++;
      charger(fav).then(res => {
        enCours--;
        const page = pageFav();
        const li = page && page.querySelector(`[data-ef-item="${fav.id}"]`);
        if (res.html) {
          cacheCartes.set(fav.id, res.html);
          if (li) remplir(li, res.html); // l'observateur allume son cœur
        } else if (res.disparu) {
          // produit supprimé ou dépublié : on le retire discrètement de la liste
          if (li) li.remove();
          const liste = lire();
          const i = index(liste, fav.id);
          if (i >= 0) { liste.splice(i, 1); ecrire(liste); }
        } else if (li) {
          li.remove();
        }
        mettreAJourPage();
        lancerChargements();
      });
    }
  }

  function mettreAJourPage(liste) {
    const page = pageFav();
    if (!page) return;
    liste = liste || memoire;
    const grille = page.querySelector('[data-ef-grille]');
    const vide = page.querySelector('[data-ef-vide]');
    const compte = page.querySelector('[data-ef-page-compte]');
    const n = liste.length;
    if (compte) {
      const modele = n > 1 ? page.getAttribute('data-texte-plusieurs') : page.getAttribute('data-texte-un');
      compte.textContent = n ? (modele || '[count]').replace('[count]', n) : '';
    }
    // les cartes en train de s'effacer restent visibles jusqu'au bout de leur fondu
    const total = grille ? grille.querySelectorAll('[data-ef-item]').length : 0;
    if (vide) vide.hidden = n > 0 || total > 0;
    if (grille) grille.hidden = total === 0;
    page.classList.add('ef-page--pret');
  }

  function rendrePage(liste, avecAnimation) {
    const page = pageFav();
    if (!page) return;
    const grille = page.querySelector('[data-ef-grille]');
    if (!grille) return;
    const ids = liste.map(f => f.id);
    // 1. retirer les cartes qui ne sont plus dans la liste
    grille.querySelectorAll('[data-ef-item]').forEach(li => {
      if (ids.includes(li.getAttribute('data-ef-item')) || li.classList.contains('ef-page__item--sortie')) return;
      if (avecAnimation) {
        li.classList.add('ef-page__item--sortie');
        setTimeout(() => { li.remove(); mettreAJourPage(); }, 260);
      } else {
        li.remove();
      }
    });
    // 2. ajouter les nouvelles, dans l'ordre de la liste
    liste.forEach((fav, i) => {
      let li = grille.querySelector(`[data-ef-item="${fav.id}"]`);
      if (li && li.classList.contains('ef-page__item--sortie')) { li.remove(); li = null; }
      if (!li) {
        li = carteAttente(fav.id);
        if (!(cacheCartes.has(fav.id) && remplir(li, cacheCartes.get(fav.id)))) fileAttente.push(fav);
      }
      const voisins = [...grille.querySelectorAll('[data-ef-item]:not(.ef-page__item--sortie)')];
      if (voisins[i] !== li) grille.insertBefore(li, voisins[i] || null);
    });
    mettreAJourPage(liste);
    lancerChargements();
  }

  document.addEventListener('elite-favoris:change', e => {
    if (pageFav()) rendrePage(e.detail.liste, true);
  });

  // ---------- Démarrage ----------
  function demarrer() {
    lire();
    synchroniser(); // déclenche aussi le premier rendu de la page « Mes favoris »
    chargerCompte();
  }

  // Nouveaux cœurs insérés par le thème (filtres, « voir plus », recommandations, aperçu rapide)
  let prevu = false;
  const observateur = new MutationObserver(mutations => {
    if (prevu) return;
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (n.nodeType === 1 && (n.matches('[data-ef-fav], [data-ef-compte]') || n.querySelector('[data-ef-fav], [data-ef-compte]'))) {
          prevu = true;
          requestAnimationFrame(() => {
            prevu = false;
            const ids = new Set(memoire.map(f => f.id));
            document.querySelectorAll('[data-ef-fav]').forEach(b => b.setAttribute('aria-pressed', String(ids.has(b.getAttribute('data-ef-fav')))));
            document.querySelectorAll('[data-ef-compte]').forEach(el => { el.textContent = memoire.length > 99 ? '99+' : String(memoire.length); el.hidden = !memoire.length; });
          });
          return;
        }
      }
    }
  });

  // Autre onglet, ou retour arrière (page restaurée depuis le cache du navigateur)
  window.addEventListener('storage', e => { if (e.key === CLE) { lire(); synchroniser(); } });
  window.addEventListener('pageshow', e => { if (e.persisted) { lire(); synchroniser(); } });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
  observateur.observe(document.documentElement, { childList: true, subtree: true });

  window.EliteFavoris = { lire, ajouter, retirer };
})();
